import { useCallback, useState } from "react";

import { Button, InfoBar, Menu, Modal } from "@shapr3d/frontend-lib";

import { zendeskNewTicketUrl } from "../../../config";
import useNavigation from "../../../hooks/navigation";
import { useMutateChargebeeAutoRenew } from "../../../hooks/useAccount";
import { useTeam } from "../../../hooks/useTeams";
import {
  type SeatBasedTeamSubscription,
  SeatBasedTeamSubscriptionOrigins,
} from "../../../hooks/useTeamsTypes";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import {
  ElementClickAction,
  ElementClickLocation,
  ModalShownAction,
} from "../../../lib/TrackingAdapter";
import { SubscriptionTier, SubscriptionType } from "../../../types";
import CTARow from "../../CTARow/CTARow";
import { CTARowDivider } from "../../CTARowDivider/CTARowDivider";
import { GenericErrorModal } from "../../Modal/Modal";
import { AddSeatsModal } from "../AddSeatsModal/AddSeatsModal";
import { SubscriptionCancelModal } from "../SubscriptionCancelModal/SubscriptionCancelModal";
import { SubscriptionExpirationText } from "../SubscriptionExpirationText/SubscriptionExpirationText";
import {
  formatExpirationDate,
  getPlanDisplayName,
  getSourceDisplayName,
} from "../subscriptionUtils";
import { TeamGracePeriodBanner } from "../TeamGracePeriodBanner/TeamGracePeriodBanner";
import { UpgradeToStudioModal } from "../UpgradeToStudioModal/UpgradeToStudioModal";

import style from "./SeatBasedSubscriptionInfo.module.css";

export const SeatBasedSubscriptionInfo: React.FC<{
  subscription: SeatBasedTeamSubscription;
  teamID: string;
}> = ({ subscription, teamID }) => {
  const [isAutoRenewCancelModalOpen, setIsAutoRenewCancelModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isAddSeatsModalOpen, setIsAddSeatsModalOpen] = useState(false);
  const [isCancelSeatsModalOpen, setIsCancelSeatsModalOpen] = useState(false);
  const [isCancelledModalOpen, setIsCancelledModalOpen] = useState(false);
  const [isGracePeriodModalOpen, setIsGracePeriodModalOpen] = useState(false);
  const [isUpgradeToStudioModalOpen, setIsUpgradeToStudioModalOpen] = useState(false);

  const subscriptionType =
    subscription.origin === SeatBasedTeamSubscriptionOrigins.Chargebee
      ? SubscriptionType.Chargebee
      : SubscriptionType.Team;

  const hasNonAutorenewingTeamSubscription = subscription.autoRenewal === false;
  const hasGracePeriodTeamSubscription = Boolean(subscription?.gracePeriodExpirationDate);

  const { t, m } = useTranslation();
  const { teamData } = useTeam(teamID);
  const tracker = useTracker();
  const navigation = useNavigation();

  const { mutate: mutateAutoRenew, isPending: isAutoRenewPending } = useMutateChargebeeAutoRenew({
    teamID,
  });

  const handleUpdatePaymentInfo = () => {
    tracker.trackButtonClick({
      action: ElementClickAction.UPDATE_PAYMENT_INFORMATION,
      location: ElementClickLocation.AUTO_RENEWAL_MANAGE_MENU,
    });
    navigation.goToTeamBilling(teamID);
  };

  const handleAutoRenewChange = useCallback(
    (autoRenew: boolean) => {
      const teamSubscriptionID = subscription.id;
      mutateAutoRenew(
        {
          autoRenew,
          teamSubscriptionID,
        },
        {
          onError: () => setIsErrorModalOpen(true),
          onSettled: () => {
            setIsAutoRenewCancelModalOpen(false);
          },
        }
      );
    },
    [subscription, mutateAutoRenew]
  );

  const handleAddSeats = useCallback(() => {
    if (hasNonAutorenewingTeamSubscription) {
      tracker.trackModalShown(ModalShownAction.ADD_SEAT_UNAVAILABLE, "no_auto_renewal");
      setIsCancelledModalOpen(true);
      return;
    }

    if (hasGracePeriodTeamSubscription) {
      tracker.trackModalShown(ModalShownAction.ADD_SEAT_UNAVAILABLE, "grace_period");
      setIsGracePeriodModalOpen(true);
      return;
    }

    tracker.trackButtonClick({
      action: ElementClickAction.ADD_SEATS,
      location: ElementClickLocation.MANAGE_SEATS_MENU,
    });
    setIsAddSeatsModalOpen(true);
  }, [hasGracePeriodTeamSubscription, hasNonAutorenewingTeamSubscription, tracker]);

  return (
    <>
      <TeamGracePeriodBanner
        className={style.gracePeriodBanner}
        subscription={subscription}
        handleUpdatePaymentInfo={handleUpdatePaymentInfo}
      />
      <CTARow
        title="Subscription"
        text={getPlanDisplayName(
          subscriptionType,
          subscription.subscriptionPeriod,
          subscription.tier,
          teamData?.name
        )}
        CTA={
          subscription.tier === SubscriptionTier.Individual &&
          !subscription.gracePeriodExpirationDate ? (
            <Button variant="borderless" onClick={() => setIsUpgradeToStudioModalOpen(true)}>
              {t(m.UpgradeToStudioModal.UpgradeButton)}
            </Button>
          ) : undefined
        }
      />
      <CTARowDivider />
      {subscription.tier !== SubscriptionTier.Individual && (
        <>
          <CTARow
            title="Seats"
            text={subscription.editorSeats}
            CTA={
              <Menu
                activator={
                  <Button
                    variant="borderless"
                    onClick={() =>
                      tracker.trackButtonClick({
                        action: ElementClickAction.MANAGE_SEATS,
                      })
                    }
                  >
                    Manage
                  </Button>
                }
              >
                <Menu.Item onClick={handleAddSeats} closeOnClick>
                  Add seats
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    setIsCancelSeatsModalOpen(true);
                    tracker.trackButtonClick({
                      action: ElementClickAction.CANCEL_SEATS,
                      location: ElementClickLocation.MANAGE_SEATS_MENU,
                    });
                  }}
                  closeOnClick
                >
                  Cancel seat
                </Menu.Item>
              </Menu>
            }
          />
          <CTARowDivider />
        </>
      )}
      <CTARow
        title={
          <SubscriptionExpirationText
            autoRenews={subscription.autoRenewal}
            subscriptionType={subscriptionType}
            expirationDate={new Date(subscription.expirationDate)}
          />
        }
        text={formatExpirationDate(subscription.expirationDate)}
        notification={
          !subscription.autoRenewal && (
            <InfoBar
              text="Turn on auto-renewal to prevent losing access to Pro features at the end of the billing cycle."
              variant="attention"
              cta={
                <Button
                  variant="grayscale"
                  onClick={() => {
                    handleAutoRenewChange(true);
                    tracker.trackButtonClick({ action: ElementClickAction.TURN_ON_AUTO_RENEWAL });
                  }}
                  disabled={isAutoRenewPending}
                >
                  Turn on auto-renewal
                </Button>
              }
            />
          )
        }
        CTA={
          subscription.autoRenewal && (
            <Menu
              activator={
                <Button
                  variant="borderless"
                  style={{ height: "100%" }}
                  onClick={() => {
                    tracker.trackButtonClick({ action: ElementClickAction.MANAGE_AUTO_RENEWAL });
                  }}
                >
                  Manage
                </Button>
              }
            >
              <Menu.Item
                onClick={() => {
                  setIsAutoRenewCancelModalOpen(true);
                  tracker.trackButtonClick({
                    action: ElementClickAction.CANCEL_SUBSCRIPTION,
                    location: ElementClickLocation.AUTO_RENEWAL_MANAGE_MENU,
                  });
                }}
                disabled={isAutoRenewPending}
                closeOnClick
              >
                <div style={{ color: "var(--control-error-label)" }}>Cancel subscription</div>
              </Menu.Item>

              <Menu.Item onClick={handleUpdatePaymentInfo} closeOnClick>
                Update payment information
              </Menu.Item>
            </Menu>
          )
        }
      />
      <CTARowDivider />
      <CTARow title="Purchased via" text={getSourceDisplayName(subscriptionType)} />
      <Modal
        title="Add Seats"
        size="small"
        open={isCancelledModalOpen}
        onClose={() => setIsCancelledModalOpen(false)}
      >
        <div className={style.notAvailableModal}>
          <span>Adding seats to {teamData?.name} is unavailable.</span>
          <div className={style.message}>
            <span>Your subscription has been cancelled.</span>
            <span>Turn on auto-renewal to add seats to your team.</span>
          </div>
        </div>
      </Modal>
      <Modal
        title="Add Seats"
        size="small"
        open={isGracePeriodModalOpen}
        onClose={() => setIsGracePeriodModalOpen(false)}
      >
        <div className={style.notAvailableModal}>
          <span>Adding seats to {teamData?.name} is unavailable.</span>
          <div className={style.message}>
            <span>Your subscription is within the grace period.</span>
            <span>Update your card details to add new seats.</span>
          </div>
        </div>
      </Modal>
      <Modal
        title="Cancel seat"
        size="small"
        open={isCancelSeatsModalOpen}
        onClose={() => setIsCancelSeatsModalOpen(false)}
      >
        <div className={style.notAvailableModal}>
          <div className={style.message}>
            <span>Please contact support if you want to remove seats from your team.</span>
          </div>
          <Button
            onClick={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.GO_TO_CONTACT_SUPPORT,
                location: ElementClickLocation.CANCEL_SEATS_MODAL,
              });
              navigation.openUrlWithZendeskAuth(zendeskNewTicketUrl);
            }}
          >
            Contact support
          </Button>
        </div>
      </Modal>
      <SubscriptionCancelModal
        subscriptionExpirationDate={formatExpirationDate(subscription.expirationDate)}
        open={isAutoRenewCancelModalOpen}
        onClose={() => setIsAutoRenewCancelModalOpen(false)}
        isLoading={isAutoRenewPending}
        editorSeatCount={subscription.editorSeats}
        isSubscriptionInGracePeriod={Boolean(subscription.gracePeriodExpirationDate)}
        onCancelSubscription={() => handleAutoRenewChange(false)}
      />
      <AddSeatsModal
        subscription={subscription}
        teamID={teamID}
        open={isAddSeatsModalOpen}
        onClose={() => setIsAddSeatsModalOpen(false)}
      />
      <GenericErrorModal open={isErrorModalOpen} onClose={() => setIsErrorModalOpen(false)} />
      {isUpgradeToStudioModalOpen && (
        <UpgradeToStudioModal
          open
          onClose={() => setIsUpgradeToStudioModalOpen(false)}
          subscription={subscription}
          teamID={teamID}
        />
      )}
    </>
  );
};
